/**
 * Client-side authentication helpers.
 *
 * All API calls use credentials: "include" so the browser forwards the
 * HttpOnly session cookie automatically. The frontend never reads the token.
 */

// On the server (SSR / Server Components) use the internal backend URL directly.
// In the browser use a relative path so the request goes through the Next.js
// rewrite proxy — same origin, no CORS.
const API_URL =
  typeof window === "undefined"
    ? (process.env.BACKEND_INTERNAL_URL ?? "http://localhost:8000")
    : "";

export interface AuthUser {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  status: "pending_verification" | "active" | "suspended";
  role: "receptionist" | "doctor" | "admin" | "support";
  is_admin: boolean;
  is_support?: boolean;
  language_preference: string | null;
  two_factor_enabled?: boolean;
  must_reset_password?: boolean;
  /** True when this session is an impersonation session (admin acting as another user). */
  is_impersonation?: boolean;
  /** The admin user's ID when is_impersonation is true. */
  impersonator_id?: string | null;
}

/**
 * Calls POST /api/auth/logout and redirects the browser to /login.
 * Intended for use in Client Components (event handlers).
 */
export async function logout(): Promise<void> {
  // El hilo del chat guardado antes de ir a conectar una cuenta no pasa a quien entre después en
  // esta pestaña (auditoría de seguridad 037).
  try {
    sessionStorage.removeItem("panel.hilo");
  } catch {
    // Sin almacenamiento de sesión no hay nada que borrar.
  }
  await fetch(`${API_URL}/api/auth/logout`, {
    method: "POST",
    credentials: "include",
  });
  // Hard navigation to ensure cookie state is fully cleared on the new page.
  window.location.href = "/login";
}

/**
 * Returns the currently authenticated user, or null if no valid session exists.
 *
 * Safe to call from both Server Components (pass `cookieHeader` from the
 * incoming request) and Client Components (omit `cookieHeader`; browser sends
 * the cookie automatically via credentials: "include").
 */
export async function getCurrentUser(
  cookieHeader?: string
): Promise<AuthUser | null> {
  try {
    const headers: HeadersInit = { "Content-Type": "application/json" };
    if (cookieHeader) {
      headers["Cookie"] = cookieHeader;
    }

    const res = await fetch(`${API_URL}/api/auth/me`, {
      method: "GET",
      credentials: "include",
      headers,
      // Opt out of Next.js fetch cache so we always get a fresh session check.
      cache: "no-store",
    });

    if (!res.ok) return null;

    return (await res.json()) as AuthUser;
  } catch {
    return null;
  }
}

export interface TwoFactorStatus {
  enabled: boolean;
  status: string;
}

export interface TwoFactorEnrollStartResponse {
  secret: string;
  otpauth_uri: string;
  backup_codes: string[];
}

export async function getTwoFactorStatus(): Promise<TwoFactorStatus | null> {
  const res = await fetch(`${API_URL}/api/auth/2fa/status`, {
    credentials: "include",
    cache: "no-store",
  });
  if (!res.ok) return null;
  return (await res.json()) as TwoFactorStatus;
}

export async function startTwoFactorEnrollment(): Promise<TwoFactorEnrollStartResponse> {
  const res = await fetch(`${API_URL}/api/auth/2fa/enroll/start`, {
    method: "POST",
    credentials: "include",
  });
  if (!res.ok) throw new Error("No se pudo iniciar la activación de 2FA.");
  return (await res.json()) as TwoFactorEnrollStartResponse;
}

export async function verifyTwoFactorEnrollment(code: string): Promise<boolean> {
  const res = await fetch(`${API_URL}/api/auth/2fa/enroll/verify`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  return res.ok;
}

export async function disableTwoFactor(password: string, code: string): Promise<boolean> {
  const res = await fetch(`${API_URL}/api/auth/2fa/disable`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password, code }),
  });
  return res.ok;
}

export async function changePassword(
  currentPassword: string,
  newPassword: string
): Promise<{ ok: true } | { error: string; body?: unknown }> {
  try {
    const res = await fetch(`${API_URL}/api/auth/change-password`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });
    if (res.ok) return { ok: true };
    const body = (await res.json().catch(() => ({}))) as unknown;
    return { error: "No se pudo actualizar tu contraseña.", body };
  } catch {
    return { error: "No se pudo conectar. Revisa tu conexión." };
  }
}
